<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useProposalsStore } from 'dashboard/stores/proposals';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedContact: { type: Object, required: true },
});

const { t } = useI18n();
const store = useProposalsStore();
// Fetched once by the parent (ContactManageView) for this contact - not
// re-fetched here to avoid a redundant duplicate request alongside
// ContactProposalSummary, which reads the same store.
const proposals = computed(() =>
  store.records
    .filter(p => p.contact_id === props.selectedContact.id)
    .sort((a, b) => b.id - a.id)
);
const uiFlags = computed(() => store.uiFlags);
const sendingEmailId = ref(null);
const sendingWhatsappId = ref(null);

const DELIVERY_BADGES = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  generated: 'bg-n-slate-3 text-n-slate-11',
  email_sent: 'bg-n-amber-3 text-n-amber-11',
  whatsapp_sent: 'bg-n-amber-3 text-n-amber-11',
  sent: 'bg-n-teal-3 text-n-teal-11',
};
const deliveryBadgeClass = state =>
  DELIVERY_BADGES[state] || DELIVERY_BADGES.draft;
const deliveryLabel = state =>
  t(`PROPOSALS_MGMT.DELIVERY_STATE.${state.toUpperCase()}`);

const sendEmail = async proposal => {
  sendingEmailId.value = proposal.id;
  try {
    await store.sendEmail(proposal.id);
    useAlert(t('PROPOSALS_MGMT.SEND_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.SEND_ERROR'));
  } finally {
    sendingEmailId.value = null;
  }
};

const sendWhatsapp = async proposal => {
  sendingWhatsappId.value = proposal.id;
  try {
    await store.sendWhatsapp(proposal.id);
    useAlert(t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_ERROR'));
  } finally {
    sendingWhatsappId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-4">
    <p v-if="uiFlags.fetchingList" class="text-sm text-n-slate-11">
      {{ t('PROPOSALS_MGMT.CONTACT_SIDEBAR.LOADING') }}
    </p>
    <p v-else-if="!proposals.length" class="text-sm text-n-slate-11">
      {{ t('PROPOSALS_MGMT.CONTACT_SIDEBAR.NO_DATA') }}
    </p>
    <div
      v-for="proposal in proposals"
      :key="proposal.id"
      class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">{{
          proposal.reference
        }}</span>
        <span
          class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
          :class="deliveryBadgeClass(proposal.delivery_state)"
        >
          {{ deliveryLabel(proposal.delivery_state) }}
        </span>
      </div>
      <span class="text-xs text-n-slate-11"
        >{{ proposal.product_name }} - {{ proposal.plan_name }}</span
      >
      <span class="text-sm text-n-slate-12"
        >{{ proposal.currency }} {{ proposal.year_1_total }}
        <span
          v-if="Number(proposal.recurring_annual_total) > 0"
          class="text-xs font-normal text-n-slate-11"
        >
          + {{ proposal.currency }} {{ proposal.recurring_annual_total }}
          {{ t('PROPOSALS_MGMT.PER_YEAR') }}
          {{ t('PROPOSALS_MGMT.CONTACT_SIDEBAR.FROM_YEAR_2') }}
        </span>
      </span>
      <div class="flex items-center gap-2">
        <a
          v-if="proposal.pdf_url"
          :href="proposal.pdf_url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs font-medium text-n-blue-11 hover:underline"
        >
          {{ t('PROPOSALS_MGMT.DOWNLOAD_PDF') }}
        </a>
        <Button
          v-if="!proposal.email_sent_at"
          :label="t('PROPOSALS_MGMT.SEND')"
          size="sm"
          :is-loading="sendingEmailId === proposal.id"
          @click="sendEmail(proposal)"
        />
        <Button
          v-if="!proposal.whatsapp_sent_at"
          :label="t('PROPOSALS_MGMT.SEND_WHATSAPP')"
          size="sm"
          variant="outline"
          :is-loading="sendingWhatsappId === proposal.id"
          @click="sendWhatsapp(proposal)"
        />
      </div>
    </div>
  </div>
</template>
